import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTelegram } from '../contexts/TelegramContext';
import { userAPI } from '../services/api';
import {
  ChartBarIcon,
  CloudArrowDownIcon,
  CloudArrowUpIcon,
  CurrencyDollarIcon,
  UserIcon,
  CalendarIcon,
  RocketLaunchIcon,
  ClipboardIcon,
  ExclamationTriangleIcon,
  ShoppingCartIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { formatBytes, formatDate } from '../utils/format';
import { SubscriptionInfo } from '../types';
import { showToast } from '../utils/toast';
import { ClientDownloadCards } from '../components/ClientDownloadCards';


export const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isInTelegram, user: telegramUser, openLink } = useTelegram();
  const [stats, setStats] = useState<number[] | null>(null);
  const [showNotifications, setShowNotifications] = useState(true);
  const [subInfo, setSubInfo] = useState<SubscriptionInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsResponse, subResponse] = await Promise.all([
        userAPI.getStats(),
        userAPI.getSubInfo()
      ]);
      setStats(statsResponse.data.data);
      setSubInfo(subResponse.data.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // 优先使用订阅信息中的流量数据，如果没有则使用用户信息中的数据
  const currentU = subInfo?.u ?? 0;
  const currentD = subInfo?.d ?? 0;
  const totalTransfer = subInfo?.transfer_enable ?? user?.transfer_enable ?? 0;
  const usedTransfer = currentU + currentD;
  const usagePercentage = totalTransfer > 0 ? (usedTransfer / totalTransfer) * 100 : 0;
  const remainingData = totalTransfer - usedTransfer;

  // 判断用户是否有有效套餐
  const hasValidPlan = subInfo?.plan_id && subInfo?.plan?.name;
  const isExpired = subInfo?.expired_at ? new Date(subInfo.expired_at * 1000) < new Date() : false;
  const hasActiveSubscription = hasValidPlan && !isExpired;
  const subscribeUrl = subInfo?.subscribe_url || '';

  const goToPlans = () => {
    navigate('/plans');
  };

  const encodeToBase64 = (value: string) => {
    const bytes = new TextEncoder().encode(value);
    let binary = '';
    bytes.forEach((byte) => {
      binary += String.fromCharCode(byte);
    });
    return btoa(binary);
  };

  const openClientImport = (schemeUrl: string) => {
    if (isInTelegram) {
      copySubscribeUrl();
      showToast('Telegram 小程序不支持直接唤起第三方客户端，已为您复制订阅链接');
      return;
    }
    window.location.href = schemeUrl;
  };

  const openCurrentPageInBrowser = () => {
    if (isInTelegram) {
      openLink(window.location.href);
      return;
    }
    window.open(window.location.href, '_blank', 'noopener,noreferrer');
  };

  const copySubscribeUrl = () => {
    if (hasActiveSubscription && subscribeUrl) {
      navigator.clipboard.writeText(subscribeUrl).then(() => {
        showToast('订阅链接已复制到剪贴板');
      });
      return;
    }
    showToast('请先订阅套餐');
  };

  const clientImports = hasActiveSubscription && subscribeUrl ? [
    {
      name: 'Clash / Mihomo',
      description: 'Windows / macOS / Android',
      schemeUrl: `clash://install-config?url=${encodeURIComponent(subscribeUrl)}`
    },
    {
      name: 'Stash',
      description: 'iOS',
      schemeUrl: `stash://install-config?url=${encodeURIComponent(subscribeUrl)}`
    },
    {
      name: 'Shadowrocket',
      description: 'iOS',
      schemeUrl: `shadowrocket://add/sub://${encodeToBase64(subscribeUrl)}`
    },
    {
      name: 'Surge',
      description: 'macOS / iOS',
      schemeUrl: `surge:///install-config?url=${encodeURIComponent(subscribeUrl)}`
    },
    {
      name: 'Loon',
      description: 'iOS',
      schemeUrl: `loon://import?url=${encodeURIComponent(subscribeUrl)}`
    },
    {
      name: 'Quantumult X',
      description: 'iOS',
      schemeUrl: `quantumult-x:///update-configuration?remote-resource=${encodeURIComponent(subscribeUrl)}`
    },
    {
      name: 'Quantumult',
      description: 'iOS',
      schemeUrl: `quantumult://configuration?server=${encodeURIComponent(subscribeUrl)}`
    }
  ] : [];

  // 解析统计数据
  const unpaidOrdersCount = stats?.[0] || 0;
  const activeTicketsCount = stats?.[1] || 0;
  const invitedUsersCount = stats?.[2] || 0;

  // 续费提醒逻辑
  const shouldShowRenewalReminder = hasActiveSubscription && (() => {
    // 检查流量使用是否超过80%
    const isHighUsage = usagePercentage >= 80;
    
    // 检查是否在有效期7天内
    const isNearExpiry = subInfo?.expired_at ? (() => {
      const expiryDate = new Date(subInfo.expired_at * 1000);
      const now = new Date();
      const daysUntilExpiry = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      return daysUntilExpiry <= 7 && daysUntilExpiry > 0;
    })() : false;
    
    return isHighUsage || isNearExpiry;
  })();
  
  // 获取续费提醒的具体原因
  const getRenewalReason = () => {
    const reasons = [];
    if (usagePercentage >= 80) {
      reasons.push(`流量已使用${usagePercentage.toFixed(1)}%`);
    }
    if (subInfo?.expired_at) {
      const expiryDate = new Date(subInfo.expired_at * 1000);
      const now = new Date();
      const daysUntilExpiry = Math.ceil((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      if (daysUntilExpiry <= 7 && daysUntilExpiry > 0) {
        reasons.push(`套餐将在${daysUntilExpiry}天后到期`);
      }
    }
    return reasons;
  };

  // 检查是否有需要提醒的内容
  const hasNotifications = unpaidOrdersCount > 0 || activeTicketsCount > 0 || invitedUsersCount > 0;

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white transition-colors">仪表板</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 transition-colors">
          欢迎回来，这里是您的账户概览。
        </p>
      </div>

      {/* 续费提醒卡片 */}
      {shouldShowRenewalReminder && (
        <div className="bg-gradient-to-r from-orange-50 to-red-50 border-2 border-orange-300 rounded-lg shadow-lg mb-8">
          <div className="px-6 py-6">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center animate-pulse">
                  <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-orange-800 mb-2 flex items-center">
                  🔔 续费提醒
                </h3>
                <div className="space-y-2 mb-4">
                  {getRenewalReason().map((reason, index) => (
                    <p key={index} className="text-base text-orange-700 font-medium">
                      • {reason}
                    </p>
                  ))}
                </div>
                <p className="text-sm text-orange-600 mb-4">
                  为避免服务中断，建议您及时续费或升级套餐
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={goToPlans}
                    className="inline-flex items-center px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-lg transition-colors shadow-sm"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    立即续费
                  </button>
                  <button
                    onClick={goToPlans}
                    className="inline-flex items-center px-6 py-3 bg-white border-2 border-orange-300 text-orange-700 text-sm font-medium rounded-lg hover:bg-orange-50 transition-colors"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                    </svg>
                    升级套餐
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 通知提醒卡片 */}
      {showNotifications && hasNotifications && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm mb-8 transition-colors">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center transition-colors">
                <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 mr-2" />
                待处理事项
              </h3>
              <button
                onClick={() => setShowNotifications(false)}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* 未支付订单 */}
              {unpaidOrdersCount > 0 && (
                <div
                  onClick={() => window.location.href = '/orders'}
                  className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 cursor-pointer hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                >
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <ShoppingCartIcon className="h-6 w-6 text-red-500" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-red-800 dark:text-red-400">
                        {unpaidOrdersCount} 个未支付订单
                      </p>
                      <p className="text-xs text-red-600 dark:text-red-500">
                        点击查看并处理
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* 进行中工单 */}
              {activeTicketsCount > 0 && (
                <div
                  onClick={() => window.location.href = '/ticket'}
                  className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
                >
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <svg className="h-6 w-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-blue-800 dark:text-blue-400">
                        {activeTicketsCount} 个进行中工单
                      </p>
                      <p className="text-xs text-blue-600 dark:text-blue-500">
                        点击查看回复
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* 邀请注册用户 */}
              {invitedUsersCount > 0 && (
                <div
                  onClick={() => window.location.href = '/invite'}
                  className="bg-green-50 border border-green-200 rounded-lg p-4 cursor-pointer hover:bg-green-100 transition-colors"
                >
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <UserIcon className="h-6 w-6 text-green-500" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-green-800">
                        {invitedUsersCount} 个邀请用户
                      </p>
                      <p className="text-xs text-green-600">
                        获得最低30%销售奖励
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 无套餐用户购买引导 */}
      {!hasActiveSubscription && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg mb-8">
          <div className="px-6 py-8">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-8 w-8 text-amber-500" />
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {isExpired ? '套餐已过期' : '您还没有订阅套餐'}
                </h3>
                <p className="text-gray-600 mb-4">
                  {isExpired
                    ? '您的套餐已过期，请续费或选择新套餐以继续使用服务。'
                    : '订阅套餐后即可享受高速稳定的网络服务，多种套餐任您选择。'
                  }
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={goToPlans}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
                  >
                    <ShoppingCartIcon className="h-4 w-4 mr-2" />
                    {isExpired ? '立即续费' : '选择套餐'}
                  </button>
                  {/* <div className="flex items-center text-sm text-gray-500">
                    <SparklesIcon className="h-4 w-4 mr-1" />
                    <span>新用户首月享受8折优惠</span>
                  </div> */}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Usage Chart */}
      <div className="bg-white shadow rounded-lg mb-8">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            流量使用情况
          </h3>
          {hasActiveSubscription ? (
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm text-gray-600 mb-1">
                  <span>已使用: {formatBytes(usedTransfer)}</span>
                  <span>总流量: {formatBytes(totalTransfer)}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min(usagePercentage, 100)}%` }}
                  ></div>
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>{usagePercentage.toFixed(1)}% 已使用</span>
                  <span>剩余: {formatBytes(Math.max(remainingData, 0))}</span>
                </div>
                {subInfo?.plan.name && (
                  <div className="mt-2 text-xs text-gray-500">
                    套餐: {subInfo.plan.name}
                    {subInfo.reset_day !== null ? (
                      <span className="ml-2">{subInfo.reset_day !== 0 ? `流量将在${subInfo.reset_day}日后重置` : '流量已在今日重置'}</span>
                    ) : null}
                    {subInfo.expired_at ? (
                      <span className="ml-2">到期时间: {hasValidPlan
                        ? (subInfo?.expired_at || user?.expired_at)
                          ? (isExpired ? '已过期' : formatDate(subInfo?.expired_at || user?.expired_at || ''))
                          : '长期有效'
                        : '未订阅'
                      }</span>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-400 mb-2">
                <ChartBarIcon className="h-12 w-12 mx-auto" />
              </div>
              <p className="text-gray-500 text-sm">订阅套餐后查看流量使用情况</p>
            </div>
          )}
        </div>
      </div>


      {/* 官方客户端下载 */}
      <div className="bg-white shadow rounded-lg mb-8">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            官方客户端下载
          </h3>
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md flex items-center space-x-3">
              <div className="flex-shrink-0">
                <svg className="w-7 h-7 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-base font-semibold text-blue-800 mb-1">客户端使用中如果遇到问题，请到使用教程中查看解决方法</div>
                {(user?.email.endsWith('@telegram.user') ||user?.email.endsWith('@strongswans.net')) &&(<ol className="list-decimal list-inside text-sm text-blue-700 mb-1">
                  <li>下载并安装客户端</li>
                  <li>登录邮箱：<code className="font-mono bg-gray-100 px-1 py-0.5 rounded text-blue-900">{user?.email}</code>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (user?.email) {
                          navigator.clipboard.writeText(user.email).then(() => {
                            showToast('邮箱地址已复制到剪贴板');
                          });
                        }
                      }}
                      className="ml-2 px-2 py-0.5 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded"
                    >复制</button>
                  </li>
                  <li>初始密码：与邮箱地址相同</li>
                  {/* <li>首次登录后建议前往“个人设置”修改密码</li> */}
                </ol>)}
              </div>
            </div>
          
          {!hasActiveSubscription && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
              <p className="text-sm text-amber-700">
                <ExclamationTriangleIcon className="h-4 w-4 inline mr-1" />
                订阅套餐后即可获取订阅链接，开始使用客户端
              </p>
            </div>
          )}
          <ClientDownloadCards
            showMacQuarantineTip
            iosHref="/knowledge"
            onCopyMacCommand={() => showToast('命令已复制到剪贴板')}
          />

          <div className="mt-6 border-t border-gray-200 pt-6">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <h4 className="text-base font-semibold text-gray-900">第三方客户端一键导入</h4>
              {hasActiveSubscription && (
                <button
                  onClick={copySubscribeUrl}
                  className="px-2.5 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
                >
                  复制订阅链接
                </button>
              )}
            </div>
            <p className="text-xs text-gray-600 mb-3">
              支持更多客户端快捷导入；若失败请复制链接并在客户端手动粘贴。
            </p>
            {!hasActiveSubscription ? (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-md flex items-center justify-between gap-3">
                <p className="text-sm text-amber-700">开通套餐后即可使用一键导入功能</p>
                <button
                  onClick={goToPlans}
                  className="inline-flex items-center px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium rounded-md transition-colors"
                >
                  去选套餐
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5">
                {clientImports.map((client) => (
                  <div key={client.name} className="border border-gray-200 rounded-md p-2.5">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1.5 sm:gap-2">
                      <h5 className="text-xs font-semibold text-gray-900 leading-5 break-words pr-1">{client.name}</h5>
                      <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded shrink-0 w-fit whitespace-nowrap">
                        {client.description}
                      </span>
                    </div>
                    <button
                      onClick={() => openClientImport(client.schemeUrl)}
                      className="mt-2 w-full px-2 py-1.5 bg-primary-600 hover:bg-primary-700 text-white text-xs font-medium rounded-md transition-colors"
                    >
                      一键导入
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Traffic Details */}
      {/* <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              流量统计
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <CloudArrowUpIcon className="h-5 w-5 text-green-500 mr-2" />
                  <span className="text-sm text-gray-600">上传流量</span>
                </div>
                <span className="text-sm font-medium text-gray-900">
                  {formatBytes(currentU)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <CloudArrowDownIcon className="h-5 w-5 text-blue-500 mr-2" />
                  <span className="text-sm text-gray-600">下载流量</span>
                </div>
                <span className="text-sm font-medium text-gray-900">
                  {formatBytes(currentD)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <ChartBarIcon className="h-5 w-5 text-purple-500 mr-2" />
                  <span className="text-sm text-gray-600">已使用</span>
                </div>
                <span className="text-sm font-medium text-gray-900">
                  {formatBytes(usedTransfer)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              账户信息
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">用户ID</span>
                <span className="text-sm font-medium text-gray-900">
                  #{user?.id}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">邮箱</span>
                <span className="text-sm font-medium text-gray-900">
                  {user?.email}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">订阅状态</span>
                <span className={`text-sm font-medium ${(subInfo?.plan_id || user?.plan_id) ? 'text-green-600' : 'text-gray-500'
                  }`}>
                  {(subInfo?.plan_id || user?.plan_id) ? '已订阅' : '未订阅'}
                </span>
              </div>
              {subInfo?.plan?.name && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">套餐名称</span>
                  <span className="text-sm font-medium text-gray-900">
                    {subInfo.plan.name}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div> */}
    </div>
  );
};